import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,

} from '@mui/icons-material';
import { zonesService } from '../../services/zonesService';
import { mapsAPI } from '../../services/api';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ZoneManagement() {
  const [zones, setZones] = useState([]);
  const [availableMaps, setAvailableMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedZone, setSelectedZone] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('create');
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    map_id: '',
    zone_type: '',
    building: ''
  });

  const [formData, setFormData] = useState({
    zone_code: '',
    zone_name: '',
    zone_type: 'room',
    map_id: '',
    map_area: '',
    building: '',
    coordinates: [],
    center_point: null,
    area_m2: 0,
    access_level: 'public',
    allowed_roles: [],
    max_occupancy: null,
    enable_entry_alerts: false,
    enable_exit_alerts: false,
    alert_contacts: [],
    description: ''
  });

  useEffect(() => {
    loadAvailableMaps();
  }, []);
  
  useEffect(() => {
    loadZones();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAvailableMaps = async () => {
    try {
      const response = await mapsAPI.getAll();
      const mapsData = Array.isArray(response?.data?.data) ? response.data.data : 
                      Array.isArray(response?.data) ? response.data : [];
      setAvailableMaps(mapsData);
    } catch (error) {
      console.warn('Maps API failed:', error.message);
    }
  };

  const loadZones = async () => {
    try {
      setLoading(true);
      const response = await zonesService.getZones(filters);
      setZones(response.zones || []);
    } catch (err) {
      setError('Erro ao carregar zonas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateZone = () => {
    setFormData({
      zone_code: '',
      zone_name: '',
      zone_type: 'room',
      map_id: '',
      map_area: '',
      building: 'MAIN',
      floor: 1,
      coordinates: [],
      center_point: null,
      area_m2: 0,
      access_level: 'public',
      allowed_roles: [],
      max_occupancy: null,
      enable_entry_alerts: false,
      enable_exit_alerts: false,
      alert_contacts: [],
      description: ''
    });
    setDialogType('create');
    setDialogOpen(true);
  };

  const handleEditZone = (zone) => {
    setSelectedZone(zone);
    setFormData({
      zone_code: zone.zone_code || '',
      zone_name: zone.zone_name || '',
      zone_type: zone.zone_type || 'room',
      map_id: zone.map_id || '',
      map_area: zone.map_area || '',
      building: zone.building || 'MAIN',
      floor: zone.floor || 1,
      coordinates: typeof zone.coordinates === 'string' ? JSON.parse(zone.coordinates || '[]') : (zone.coordinates || []),
      center_point: typeof zone.center_point === 'string' ? JSON.parse(zone.center_point || 'null') : zone.center_point,
      area_m2: zone.area_m2 || 0,
      access_level: zone.access_level || 'public',
      allowed_roles: typeof zone.allowed_roles === 'string' ? JSON.parse(zone.allowed_roles || '[]') : (zone.allowed_roles || []),
      max_occupancy: zone.max_occupancy,
      enable_entry_alerts: Boolean(zone.enable_entry_alerts),
      enable_exit_alerts: Boolean(zone.enable_exit_alerts),
      alert_contacts: typeof zone.alert_contacts === 'string' ? JSON.parse(zone.alert_contacts || '[]') : (zone.alert_contacts || []),
      description: zone.description || ''
    });
    setDialogType('edit');
    setDialogOpen(true);
  };

  const handleSaveZone = async () => {
    try {
      if (dialogType === 'create') {
        await zonesService.createZone(formData);
      } else {
        await zonesService.updateZone(selectedZone.id, formData);
      }
      setDialogOpen(false);
      loadZones();
    } catch (err) {
      setError('Erro ao salvar zona');
      console.error(err);
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (window.confirm('Tem certeza que deseja excluir esta zona?')) {
      try {
        await zonesService.deleteZone(zoneId);
        loadZones();
      } catch (err) {
        setError('Erro ao excluir zona');
        console.error(err);
      }
    }
  };

  const getZoneTypeColor = (type) => {
    const zoneType = zonesService.getZoneTypes().find(t => t.value === type);
    return zoneType ? zoneType.color : '#757575';
  };

  const getZoneTypeLabel = (type) => {
    const zoneType = zonesService.getZoneTypes().find(t => t.value === type);
    return zoneType ? zoneType.label : type;
  };

  const getAccessLevelLabel = (level) => {
    const accessLevel = zonesService.getAccessLevels().find(l => l.value === level);
    return accessLevel ? accessLevel.label : level;
  };

  const getOccupancyColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestão de Zonas
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateZone}
        >
          Nova Zona
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Mapa</InputLabel>
                <Select
                  value={filters.map_id}
                  label="Mapa"
                  onChange={(e) => setFilters({ ...filters, map_id: e.target.value })}
                >
                  <MenuItem value="">Todos os Mapas</MenuItem>
                  {availableMaps.map(map => (
                    <MenuItem key={map.id} value={map.id}>
                      {map.area_name || map.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Zona</InputLabel>
                <Select
                  value={filters.zone_type}
                  label="Tipo de Zona"
                  onChange={(e) => setFilters({ ...filters, zone_type: e.target.value })}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {zonesService.getZoneTypes().map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Prédio"
                value={filters.building}
                onChange={(e) => setFilters({ ...filters, building: e.target.value })}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela de Zonas */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Mapa</TableCell>
                  <TableCell>Acesso</TableCell>
                  <TableCell>Ocupação</TableCell>
                  <TableCell>Alertas</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : zones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Nenhuma zona encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  zones.map((zone) => (
                    <TableRow key={zone.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {zone.zone_code}
                        </Typography>
                      </TableCell>
                      <TableCell>{zone.zone_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={getZoneTypeLabel(zone.zone_type)}
                          size="small"
                          sx={{ 
                            backgroundColor: getZoneTypeColor(zone.zone_type),
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {availableMaps.find(m => m.id === zone.map_id)?.area_name || 
                         availableMaps.find(m => m.id === zone.map_id)?.name || 
                         zone.map_area || 
                         `Andar ${zone.floor || 1}`}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getAccessLevelLabel(zone.access_level)}
                          size="small"
                          color={zone.access_level === 'restricted' ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {zone.max_occupancy ? (
                          <Box>
                            <Typography variant="body2">
                              {zone.current_occupancy || 0}/{zone.max_occupancy}
                            </Typography>
                            <Chip
                              label={`${zone.occupancy_percentage || 0}%`}
                              size="small"
                              color={getOccupancyColor(zone.occupancy_percentage || 0)}
                            />
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {zone.current_occupancy || 0}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {zone.enable_entry_alerts && (
                            <Chip label="Entrada" size="small" color="info" />
                          )}
                          {zone.enable_exit_alerts && (
                            <Chip label="Saída" size="small" color="warning" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Visualizar">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditZone(zone)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Entidades">
                          <IconButton size="small">
                            <PeopleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteZone(zone.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog de Criação/Edição */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'create' ? 'Nova Zona' : 'Editar Zona'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Informações Básicas" />
            <Tab label="Configurações" />
            <Tab label="Alertas" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Código da Zona *"
                  value={formData.zone_code}
                  onChange={(e) => setFormData({ ...formData, zone_code: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome da Zona *"
                  value={formData.zone_name}
                  onChange={(e) => setFormData({ ...formData, zone_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo *</InputLabel>
                  <Select
                    value={formData.zone_type}
                    label="Tipo *"
                    onChange={(e) => setFormData({ ...formData, zone_type: e.target.value })}
                  >
                    {zonesService.getZoneTypes().map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Mapa *</InputLabel>
                  <Select
                    value={formData.map_id}
                    label="Mapa *"
                    onChange={(e) => {
                      const selectedMap = availableMaps.find(m => m.id === e.target.value);
                      setFormData({ 
                        ...formData, 
                        map_id: e.target.value,
                        map_area: selectedMap?.area_name || selectedMap?.name || ''
                      });
                    }}
                  >
                    {availableMaps.map(map => (
                      <MenuItem key={map.id} value={map.id}>
                        {map.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Prédio"
                  value={formData.building}
                  onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Andar"
                  type="number"
                  value={formData.floor || 1}
                  onChange={(e) => setFormData({ ...formData, floor: parseInt(e.target.value) || 1 })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Área (m²)"
                  type="number"
                  value={formData.area_m2}
                  onChange={(e) => setFormData({ ...formData, area_m2: parseFloat(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capacidade Máxima"
                  type="number"
                  value={formData.max_occupancy || ''}
                  onChange={(e) => setFormData({ ...formData, max_occupancy: e.target.value ? parseInt(e.target.value) : null })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Nível de Acesso</InputLabel>
                  <Select
                    value={formData.access_level}
                    label="Nível de Acesso"
                    onChange={(e) => setFormData({ ...formData, access_level: e.target.value })}
                  >
                    {zonesService.getAccessLevels().map(level => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Configurações de Alertas
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enable_entry_alerts}
                      onChange={(e) => setFormData({ ...formData, enable_entry_alerts: e.target.checked })}
                    />
                  }
                  label="Alertas de Entrada"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enable_exit_alerts}
                      onChange={(e) => setFormData({ ...formData, enable_exit_alerts: e.target.checked })}
                    />
                  }
                  label="Alertas de Saída"
                />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveZone}
            disabled={!formData.zone_code || !formData.zone_name}
          >
            {dialogType === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}